import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Link from "next/link";
import { msg, Trans } from "@lingui/macro";
import { Text } from "@otrium/atoms";
import { Box, Flex } from "@otrium/core";
import { useTheme, Theme } from "@emotion/react";
import { CartWarning } from "src/atoms/CartWarning";
import { CloseButton } from "src/atoms/CloseButton";
import { CounterInput } from "src/atoms/CounterInput";
import { LineItem } from "src/types/ctgraphql.d";
import { getCTAttribute } from "src/utils/ct/attributeHelper";
import { getCartItemAvailability } from "src/utils/ct/cartItemAvailabilityHelper";
import CTPrice from "src/atoms/CTPrice/CTPrice";
import { useIncreaseCartItem } from "src/hooks/useIncreaseCartItem";
import { useDecreaseCartItem } from "src/hooks/useDecreaseCartItem";
import { useDeleteCartItem } from "src/hooks/useDeleteCartItem";
import { CartItemContainer, CartItemContent } from "./CartItem.styled";
import { useSegment } from "src/hooks/useSegment";
import {
  getLineItemDiscountPrice,
  getRegularPrice,
} from "src/utils/getRegularPrice";
import { formatStandardPrice } from "src/utils/ct/priceHelper";
import { AppCtx } from "src/contexts/app.context";
import { DiscountRound } from "src/icons/DiscountRound";
import { useLingui } from "@lingui/react";
import { MessageDescriptor } from "@lingui/core";
import { DeleteOutline } from "src/icons";
import { ProductSize } from "../ProductSize";

interface CartItemProps {
  item: LineItem;
  discountCode?: string;
}

const OUT_OF_STOCK_WARNING = "This item is currently out of stock.";
const QUANTITY_EXCEEDS_WARNING =
  "Quantity exceeds availability. Please reduce quantity";

const cartWarningTranslation: Record<string, MessageDescriptor> = {
  ["OUT_OF_STOCK_WARNING"]: msg`This item is currently out of stock.`,
  ["QUANTITY_EXCEEDS_WARNING"]: msg`Quantity exceeds availability. Please reduce quantity`,
};

const HIDE_MS = 200;

const CartItem: FC<CartItemProps> = ({ item, discountCode }) => {
  const { _ } = useLingui();
  const { storeConfig } = useContext(AppCtx);
  const [fadeOut, setFadeOut] = useState(false);
  const [displayNone, setDisplayNone] = useState(false);
  const [displayNoneTimeout, setDisplayNoneTimeout] = useState<null | number>(
    null
  );
  const { segmentProductAddedOrRemoved, segmentProductOutOfStockCT } =
    useSegment();

  const theme: Theme = useTheme();

  const hideCartItem = () => {
    setFadeOut(true);
    setDisplayNoneTimeout(
      window.setTimeout(() => setDisplayNone(true), HIDE_MS)
    );
  };

  const showCartItem = () => {
    if (displayNoneTimeout) {
      clearTimeout(displayNoneTimeout);
      setDisplayNoneTimeout(null);
    }
    setFadeOut(false);
    setDisplayNone(false);
  };

  const { deleteCartItem, loading: deleteCartItemLoading } = useDeleteCartItem({
    refetchQueries: ["cart"],
    onCompleted: (decreaseCartItemData) => {
      const itemIsNotDeleted =
        decreaseCartItemData?.updateMyCart?.lineItems?.find(
          (newItem) => newItem.id === item.id
        );
      if (itemIsNotDeleted) {
        showCartItem();
      }
    },
  });

  const { increaseCartItem, loading: increaseCartItemLoading } =
    useIncreaseCartItem({ refetchQueries: ["cart"] });
  const { decreaseCartItem, loading: decreaseCartItemLoading } =
    useDecreaseCartItem({
      refetchQueries: ["cart"],
    });

  const isLoading =
    deleteCartItemLoading || decreaseCartItemLoading || increaseCartItemLoading;

  const isDisabled = isLoading; // || item.stock_available <= 0;

  const { itemAvailableQuantity, isItemExceedsAvailability, isItemOutOfStock } =
    getCartItemAvailability(item);

  const regularPrice = useMemo(
    () => getRegularPrice(item, storeConfig),
    [item, storeConfig]
  );

  const discountPrice = useMemo(() => getLineItemDiscountPrice(item), [item]);

  const isUnsellable = useMemo(
    () =>
      item.variant?.attributesRaw.some(
        (attr) => attr.name === "sellable" && !attr.value
      ),
    [item]
  );

  const getProductCount = (items: LineItem[]): number => {
    return items.reduce((count, currentItem) => {
      return count + ((currentItem.quantity as number) || 0);
    }, 0);
  };

  const onRemoveClick = async (lineItemId: string) => {
    hideCartItem();
    const response = await deleteCartItem([lineItemId]);

    void segmentProductAddedOrRemoved(
      item,
      "productRemoved",
      storeConfig.countryCode,
      undefined,
      {
        product_count: getProductCount(
          response?.data?.updateMyCart?.lineItems || []
        ),
        cart_total: formatStandardPrice(
          response?.data?.updateMyCart?.totalPrice
        ),
        totalPrice: formatStandardPrice(prices?.regularPrice),
        price: formatStandardPrice(prices?.price),
        discountPrice: prices?.discountPrice
          ? formatStandardPrice(prices?.discountPrice)
          : null,
      }
    );
  };

  const onQuantityChange = async (quantity: number) => {
    if (!item.variant?.sku || quantity > itemAvailableQuantity) {
      return;
    }

    if (quantity > item.quantity) {
      const response = await increaseCartItem(item.variant.sku, item);
      void segmentProductAddedOrRemoved(
        item,
        "productAdded",
        storeConfig.countryCode,
        1,
        {
          product_count: getProductCount(
            response?.data?.updateMyCart?.lineItems || []
          ),
          cart_total: formatStandardPrice(
            response?.data?.updateMyCart?.totalPrice
          ),
          totalPrice: formatStandardPrice(regularPrice),
          price: formatStandardPrice(prices?.price),
          discountPrice: prices?.discountPrice
            ? formatStandardPrice(prices?.discountPrice)
            : null,
        },
        response?.data?.updateMyCart?.lineItems
      );
    } else if (quantity < item.quantity) {
      const response = await decreaseCartItem(
        item.id,
        item.quantity - quantity
      );
      void segmentProductAddedOrRemoved(
        item,
        "productRemoved",
        storeConfig.countryCode,
        1,
        {
          product_count: getProductCount(
            response?.data?.updateMyCart?.lineItems || []
          ),
          cart_total: formatStandardPrice(
            response?.data?.updateMyCart?.totalPrice
          ),
          totalPrice: formatStandardPrice(regularPrice),
          price: formatStandardPrice(prices?.price),
          discountPrice: prices?.discountPrice
            ? formatStandardPrice(prices?.discountPrice)
            : null,
        },
        response?.data?.updateMyCart?.lineItems
      );
    }
  };

  useEffect(() => {
    if (isItemExceedsAvailability || isItemOutOfStock) {
      const warning = isItemOutOfStock
        ? OUT_OF_STOCK_WARNING
        : QUANTITY_EXCEEDS_WARNING;
      void segmentProductOutOfStockCT(item, warning, storeConfig.countryCode);
    }
  }, [
    isItemExceedsAvailability,
    isItemOutOfStock,
    item,
    segmentProductOutOfStockCT,
    storeConfig.countryCode,
  ]);

  const getAttribute = useCallback(
    (attribute: string) =>
      getCTAttribute(attribute, item.variant?.attributesRaw),
    [item.variant?.attributesRaw]
  );

  const prices = useMemo(
    () => ({
      regularPrice: {
        ...regularPrice,
        centAmount: regularPrice.centAmount * item.quantity,
      },
      price: {
        ...item.price.value,
        centAmount: item.price.value.centAmount * item.quantity,
      },
      discountPrice: discountPrice
        ? {
            ...discountPrice,
            centAmount: discountPrice.centAmount * item.quantity,
          }
        : null,
    }),
    [item.quantity, regularPrice, discountPrice, item.price.value]
  );

  return (
    <CartItemContainer
      data-testid="cart-item"
      flexDirection="column"
      style={{
        ...(fadeOut ? { transition: `opacity ${HIDE_MS}ms`, opacity: 0 } : {}),
        ...(displayNone ? { display: "none" } : {}),
      }}
    >
      <CartItemContent
        isDisabled={isDisabled || isItemOutOfStock || isUnsellable}
      >
        <Flex>
          <Box height="120px" width="80px">
            <Link
              href={`/product/${item.productSlug}`}
              passHref
              data-testid="cart-item-img-a"
            >
              <img
                alt={getAttribute("name") as string}
                className="lazyload skeleton"
                data-src={getAttribute("main_image")}
              />
            </Link>
          </Box>
          <Box mt={2} ml={3}>
            <Text
              data-testid="cart-item-brand-name"
              color="tone.black"
              fontWeight="600"
              lineHeight="1.5"
              letterSpacing="0.4px"
              as="p"
            >
              {getAttribute("brand_name") as string}
            </Text>
            <Link
              href={`/product/${item.productSlug}`}
              passHref
              data-testid="cart-item-product-name-a"
              style={{ color: "inherit" }}
            >
              <Text
                fontSize={0}
                lineHeight="1.43"
                letterSpacing="normal"
                sx={{
                  textTransform: "lowercase",
                  "&:first-letter": {
                    textTransform: "capitalize",
                  },
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {getAttribute("name") as string}
              </Text>
            </Link>

            <Box mt={4}>
              <Text fontSize={0} lineHeight="1.71" letterSpacing="0.35px">
                <p style={{ display: "inline-block" }}>
                  <Trans>Size</Trans>
                </p>{" "}
                <p
                  data-testid="cart-item-size"
                  style={{
                    color: "tone.black",
                    fontWeight: 600,
                    display: "inline-block",
                  }}
                >
                  <ProductSize
                    sizeSchema={getAttribute("size_schema_code") as string}
                    size={getAttribute("size_value") as string}
                    c_size={getAttribute("c_size") as string}
                  />
                </p>
              </Text>
            </Box>
            {item.discountedPricePerQuantity.length > 0 && (
              <Box display="flex" sx={{ alignItems: "center" }}>
                <DiscountRound
                  width="14"
                  color={theme.colors.tone.anthraciteLight}
                />
                <Text
                  as="span"
                  fontSize="fontSize14"
                  pl={1}
                  color={theme.colors.tone.anthraciteLight}
                >
                  <Trans>Discount applied</Trans>
                </Text>
              </Box>
            )}
          </Box>
        </Flex>
        <Flex mt={3} justifyContent="space-between">
          <Box>
            <Text fontSize={0} lineHeight="1.71" letterSpacing="0.35px">
              <p style={{ display: "inline-block" }}>
                <Trans>Quantity</Trans>
              </p>
            </Text>

            <Box mt={1} ml="-4px">
              <CounterInput
                key={`${item.id}-${fadeOut.toString()}`}
                count={item.quantity}
                max={itemAvailableQuantity}
                min={0}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onChange={onQuantityChange}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              gap: "8px",
              placeItems: "flex-end",
            }}
          >
            {prices.discountPrice ? (
              <Text
                data-testid="cart-item-discount-price"
                as="p"
                color="semantic.red"
                fontWeight="600"
                display="inline-block"
                lineHeight="1.5"
                letterSpacing="0.4px"
              >
                <CTPrice price={prices.discountPrice} />
              </Text>
            ) : null}
            <Text
              data-testid="cart-item-price"
              as="p"
              color={discountPrice ? "tone.anthraciteLight" : "semantic.red"}
              fontWeight={discountPrice ? 300 : 600}
              display="inline-block"
              lineHeight="1.5"
              letterSpacing="0.4px"
              sx={{ textDecoration: discountPrice ? "line-through" : "unset" }}
            >
              <CTPrice price={prices.price} />
            </Text>
            <Box as="span">
              <Text
                data-testid="cart-item-regular-price"
                as="p"
                color={"tone.anthraciteLight"}
                fontWeight={300}
                display="inline-block"
                sx={{ textDecoration: "line-through" }}
                lineHeight="1.5"
                letterSpacing="0.4px"
              >
                <CTPrice price={prices.regularPrice} />
              </Text>
              <Text as="span" color="tone.anthraciteLight">
                *
              </Text>
            </Box>
          </Box>
        </Flex>
        <CloseButton
          data-testid="cart-item-delete-button"
          onClick={() => void onRemoveClick(item.id)}
          fill={theme.colors.tone.anthraciteLight}
          icon={
            <DeleteOutline
              color={theme.colors.tone.anthraciteLight}
              stroke={theme.colors.tone.anthraciteLight}
            />
          }
          sx={{
            position: "absolute",
            right: "8px",
            top: "8px",
            color: `${theme.colors.tone.anthraciteLight} !important`,
          }}
        />
      </CartItemContent>
      {isItemExceedsAvailability || isItemOutOfStock || isUnsellable ? (
        <CartWarning
          warning={
            isItemOutOfStock || isUnsellable
              ? _(cartWarningTranslation.OUT_OF_STOCK_WARNING)
              : _(cartWarningTranslation.QUANTITY_EXCEEDS_WARNING)
          }
        />
      ) : null}
    </CartItemContainer>
  );
};

export default CartItem;
